export function niceIntegerTicks(maximum: number, target = 6): number[] {
  const max = Math.max(0, Math.ceil(maximum));
  if (max === 0) return [0, 1];
  if (max <= 4) return Array.from({ length: max + 1 }, (_, index) => index);
  const rough = max / Math.max(1, target - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = Math.max(1, nice * magnitude);
  const ceiling = Math.ceil(max / step) * step;
  const values: number[] = [];
  for (let value = 0; value <= ceiling; value += step) values.push(value);
  return values;
}

export interface ActivityChartTick {
  value: number;
  y: number;
}

export interface ActivityChartPoint {
  index: number;
  value: number;
  x: number;
  y: number;
}

export interface ActivityChartGeometry {
  width: number;
  height: number;
  plot: { left: number; right: number; top: number; bottom: number };
  yLabelX: number;
  dateLabelY: number;
  markerRadius: number;
  scaleMaximum: number;
  ticks: ActivityChartTick[];
  points: ActivityChartPoint[];
  labelIndexes: number[];
}

export function activityChartLabelIndexes(pointCount: number, viewportWidth: number): number[] {
  if (pointCount <= 0) return [];
  const limit = viewportWidth < 300 ? 4 : viewportWidth < 480 ? 5 : viewportWidth < 760 ? 6 : viewportWidth < 1100 ? 8 : 10;
  if (pointCount <= limit) return Array.from({ length: pointCount }, (_, index) => index);
  return [...new Set(Array.from({ length: limit }, (_, index) => Math.round(index * (pointCount - 1) / (limit - 1))))];
}

/** One coordinate model drives labels, grid lines, markers, hit areas and X labels. */
export function activityChartGeometry(values: number[], viewportWidth: number): ActivityChartGeometry {
  const width = Math.max(240, Math.round(viewportWidth) || 320);
  const height = 190;
  const markerRadius = 4;
  // Edge labels stay centered on their points; plot padding, not text anchoring,
  // keeps the first and last date inside the SVG.
  const horizontalPadding = 32;
  const plot = { left: horizontalPadding, right: width - horizontalPadding, top: 14, bottom: 150 };
  const maximum = Math.max(0, ...values);
  const tickValues = niceIntegerTicks(maximum, 5);
  const scaleMaximum = tickValues.at(-1) || 1;
  const y = (value: number) => plot.bottom - value * ((plot.bottom - plot.top) / scaleMaximum);
  const x = (index: number) => values.length === 1
    ? (plot.left + plot.right) / 2
    : plot.left + index * ((plot.right - plot.left) / Math.max(1, values.length - 1));
  return {
    width,
    height,
    plot,
    yLabelX: plot.left - 8,
    dateLabelY: 177,
    markerRadius,
    scaleMaximum,
    ticks: tickValues.map((value) => ({ value, y: y(value) })),
    points: values.map((value, index) => ({ index, value, x: x(index), y: y(value) })),
    labelIndexes: activityChartLabelIndexes(values.length, width),
  };
}

export function compactAnalyticsDate(value: string, locale: 'en' | 'ru'): string {
  const date = new Date(`${value}T12:00:00Z`);
  return locale === 'ru'
    ? new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(date)
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
}

export function compactNumericAnalyticsDate(value: string): string {
  const [, month = '', day = ''] = value.split('-');
  return `${day}.${month}`;
}

export function compactAnalyticsPeriodLabel(item: { from: string; to: string }, locale: 'en' | 'ru', edge: 'from' | 'to' = 'from'): string {
  return compactAnalyticsDate(item[edge], locale);
}

export function aggregateMobileActivitySeries<T extends { date: string; from: string; to: string; tryons: number; requests: number }>(
  data: T[], totalCalendarDays: number,
): Array<{ date: string; from: string; to: string; tryons: number; requests: number }> {
  if (totalCalendarDays < 15 || totalCalendarDays > 45 || data.length < 2) return data;
  const buckets: Array<{ date: string; from: string; to: string; tryons: number; requests: number }> = [];
  for (let index = 0; index < data.length; index += 3) {
    const group = data.slice(index, index + 3);
    buckets.push({
      date: group[0].from,
      from: group[0].from,
      to: group.at(-1)!.to,
      tryons: group.reduce((sum, item) => sum + item.tryons, 0),
      requests: group.reduce((sum, item) => sum + item.requests, 0),
    });
  }
  return buckets;
}

export function clampedChartScrollLeft(scrollLeft: number, scrollWidth: number, clientWidth: number): number {
  return Math.min(Math.max(0, scrollWidth - clientWidth), Math.max(0, scrollLeft));
}

export function analyticsPeriodLabel(item: { from: string; to: string }, granularity: 'day' | 'week' | 'month', locale: 'en' | 'ru'): string {
  const from = new Date(`${item.from}T12:00:00Z`);
  if (granularity === 'day') return from.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  if (granularity === 'month') return from.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const to = new Date(`${item.to}T12:00:00Z`);
  const sameMonth = from.getUTCMonth() === to.getUTCMonth() && from.getUTCFullYear() === to.getUTCFullYear();
  return sameMonth
    ? `${from.getUTCDate()}–${to.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}`
    : `${from.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })} – ${to.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`;
}
